import React, { useState, useEffect, useMemo } from "react";
import { courseAPI, teacherAPI } from "../services/api";
import "./CourseSwitcher.css";

const CATEGORY_ORDER = [
  "MS Math",
  "MS RLA",
  "MS Science",
  "MS Social Studies",
  "HS Math",
  "HS RLA",
  "HS Science",
  "HS Social Studies",
];

const normalize = (value) => String(value || "").toLowerCase().trim();
const defaultCategoryForCourse = (course) =>
  Number(course?.grade_level) <= 8 ? "MS Science" : "HS Science";

const findCourseIdByAliases = (courses, aliases) => {
  const normalizedAliases = aliases.map((alias) => normalize(alias));
  const exactMatch = courses.find((course) =>
    normalizedAliases.includes(normalize(course.name)),
  );
  if (exactMatch) return exactMatch.id;

  const partialMatch = courses.find((course) =>
    normalizedAliases.some((alias) => normalize(course.name).includes(alias)),
  );
  return partialMatch?.id || null;
};

const OBJECTIVE_SELECTION_PREFIX = "objective:";
const isObjectiveSelectionValue = (value) =>
  String(value || "").startsWith(OBJECTIVE_SELECTION_PREFIX);

const CourseSwitcher = ({
  currentCourse,
  onCourseChange,
  showQuickSelect = false,
  enablePrivateObjectiveToggle = false,
  enableMyClassesOnlyFilter = false,
  authProfile = null,
}) => {
  const [courses, setCourses] = useState([]);
  const [objectiveCourses, setObjectiveCourses] = useState([]);
  const [showPrivateOnly, setShowPrivateOnly] = useState(
    enablePrivateObjectiveToggle && isObjectiveSelectionValue(currentCourse),
  );
  const [showMyClassesOnly, setShowMyClassesOnly] = useState(false);
  const [myClassCourseIds, setMyClassCourseIds] = useState([]);
  const [myClassLoading, setMyClassLoading] = useState(false);
  const [loading, setLoading] = useState(true);
  const [objectiveLoading, setObjectiveLoading] = useState(false);
  const canUseMyClassesFilter =
    enableMyClassesOnlyFilter && authProfile?.role === "teacher";

  useEffect(() => {
    const loadCourses = async () => {
      try {
        const response = await courseAPI.getAll();
        setCourses(response.data);
      } catch (error) {
        console.error("Error loading courses:", error);
      } finally {
        setLoading(false);
      }
    };

    loadCourses();
  }, []);

  useEffect(() => {
    if (!enablePrivateObjectiveToggle) return;
    let cancelled = false;

    const loadObjectiveCourses = async () => {
      setObjectiveLoading(true);
      try {
        const response = await teacherAPI.listObjectiveCourses(false);
        if (cancelled) return;
        setObjectiveCourses(Array.isArray(response.data) ? response.data : []);
      } catch (error) {
        if (cancelled) return;
        console.error("Error loading private objective courses:", error);
        setObjectiveCourses([]);
      } finally {
        if (!cancelled) setObjectiveLoading(false);
      }
    };

    loadObjectiveCourses();
    return () => {
      cancelled = true;
    };
  }, [enablePrivateObjectiveToggle]);

  useEffect(() => {
    if (!enablePrivateObjectiveToggle) {
      setShowPrivateOnly(false);
      return;
    }
    if (isObjectiveSelectionValue(currentCourse)) {
      setShowPrivateOnly(true);
      return;
    }
    if (currentCourse) {
      setShowPrivateOnly(false);
    }
  }, [currentCourse, enablePrivateObjectiveToggle]);

  useEffect(() => {
    if (canUseMyClassesFilter) return;
    setShowMyClassesOnly(false);
    setMyClassCourseIds([]);
  }, [canUseMyClassesFilter]);

  useEffect(() => {
    if (!canUseMyClassesFilter) return;
    let cancelled = false;

    const loadMyClasses = async () => {
      setMyClassLoading(true);
      try {
        const response = await teacherAPI.getProfileSettings();
        if (cancelled) return;
        const periods = Array.isArray(response?.data?.periods)
          ? response.data.periods
          : [];
        const ids = [
          ...new Set(
            periods
              .filter((period) => period?.mode === "course" && period?.courseId)
              .map((period) => period.courseId),
          ),
        ];
        setMyClassCourseIds(ids);
      } catch (error) {
        if (cancelled) return;
        console.error("Error loading teacher class assignments:", error);
        setMyClassCourseIds([]);
      } finally {
        if (!cancelled) setMyClassLoading(false);
      }
    };

    loadMyClasses();
    return () => {
      cancelled = true;
    };
  }, [canUseMyClassesFilter]);

  const filteredCourses = useMemo(() => {
    if (!showMyClassesOnly) return courses;
    if (!myClassCourseIds.length) return [];
    const allowed = new Set(myClassCourseIds);
    return courses.filter((course) => allowed.has(course.id));
  }, [courses, myClassCourseIds, showMyClassesOnly]);

  const groupedCourses = CATEGORY_ORDER.reduce((acc, category) => {
    acc[category] = filteredCourses.filter(
      (course) => (course.course_category || defaultCategoryForCourse(course)) === category,
    );
    return acc;
  }, {});

  const quickSelectIds = useMemo(
    () => ({
      usHistoryId: findCourseIdByAliases(courses, [
        "United States History Studies Since 1877",
        "United States History",
        "US History",
        "U.S. History",
      ]),
      usGovernmentId: findCourseIdByAliases(courses, [
        "United States Government",
        "US Government",
        "U.S. Government",
        "Government",
      ]),
    }),
    [courses],
  );
  const isLoading = loading
    || (enablePrivateObjectiveToggle && showPrivateOnly && objectiveLoading)
    || (showMyClassesOnly && myClassLoading);
  const selectValue = useMemo(() => {
    if (showPrivateOnly) {
      return isObjectiveSelectionValue(currentCourse) ? currentCourse : "";
    }
    return isObjectiveSelectionValue(currentCourse) ? "" : currentCourse || "";
  }, [currentCourse, showPrivateOnly]);

  const handleTogglePrivate = (event) => {
    const checked = Boolean(event.target.checked);
    setShowPrivateOnly(checked);
    onCourseChange("");
  };

  const handleToggleMyClasses = (event) => {
    const checked = Boolean(event.target.checked);
    setShowMyClassesOnly(checked);
    if (!checked) return;
    if (!currentCourse || isObjectiveSelectionValue(currentCourse)) return;
    if (myClassCourseIds.includes(currentCourse)) return;
    onCourseChange("");
  };

  return (
    <div className="course-switcher" data-tour="course-switcher">
      <label htmlFor="course-select">Course:</label>
      {enablePrivateObjectiveToggle ? (
        <label className="course-private-toggle" htmlFor="course-private-toggle">
          <input
            id="course-private-toggle"
            type="checkbox"
            checked={showPrivateOnly}
            onChange={handleTogglePrivate}
          />
          Private Objective Courses
        </label>
      ) : null}
      {canUseMyClassesFilter ? (
        <div className="course-my-classes-wrap">
          <label className="course-private-toggle" htmlFor="course-my-classes-toggle">
            <input
              id="course-my-classes-toggle"
              type="checkbox"
              checked={showMyClassesOnly}
              onChange={handleToggleMyClasses}
            />
            My Classes only
          </label>
          <button
            type="button"
            className="course-info-btn"
            title="Set your teaching periods on the Profile page to filter this course list."
            aria-label="Set your teaching periods on the Profile page to filter this course list."
            onClick={() => {
              window.location.hash = "/profile";
            }}
          >
            i
          </button>
        </div>
      ) : null}
      <select
        id="course-select"
        value={selectValue}
        onChange={(e) => onCourseChange(e.target.value)}
        disabled={isLoading}
      >
        {showPrivateOnly ? (
          <>
            <option value="">Select a private objective course...</option>
            {objectiveCourses.map((course) => (
              <option
                key={course.id}
                value={`${OBJECTIVE_SELECTION_PREFIX}${course.id}`}
              >
                {course.title}
                {course.subject ? ` (${course.subject})` : ""}
              </option>
            ))}
          </>
        ) : (
          <>
            <option value="">Select a course...</option>
            {CATEGORY_ORDER.map((category) => {
              const categoryCourses = groupedCourses[category] || [];
              if (categoryCourses.length === 0) return null;
              return (
                <optgroup key={category} label={category}>
                  {categoryCourses.map((course) => (
                    <option key={course.id} value={course.id}>
                      {course.name} (Grade {course.grade_level})
                    </option>
                  ))}
                </optgroup>
              );
            })}
          </>
        )}
      </select>
      {showMyClassesOnly && !isLoading && myClassCourseIds.length === 0 ? (
        <span className="course-filter-empty">
          No classes found in Profile setup.
        </span>
      ) : null}

      {showQuickSelect && !showPrivateOnly && (
        <div className="course-quick-actions">
          <button
            type="button"
            className="course-quick-btn"
            onClick={() => onCourseChange(quickSelectIds.usHistoryId)}
            disabled={loading || !quickSelectIds.usHistoryId}
          >
            U.S. History
          </button>
          <button
            type="button"
            className="course-quick-btn"
            onClick={() => onCourseChange(quickSelectIds.usGovernmentId)}
            disabled={loading || !quickSelectIds.usGovernmentId}
          >
            U.S. Government
          </button>
        </div>
      )}
    </div>
  );
};

export default CourseSwitcher;
